import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import Modal from 'core-system/Modal'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import palette from 'core-system/Themes/palette'

// ─── Brand ────────────────────────────────────────────────────────────────────

const rpGreen = '#16a34a'
const rpGreenLight = '#dcfce7'
const rpGreenMid = '#bbf7d0'
const EMPLOYER_STIPEND = 100

// ─── Bike catalog data ────────────────────────────────────────────────────────

interface Bike {
  id: string
  name: string
  brand: string
  category: string
  tagline: string
  monthlyRate: number
  range: string
  weight: string
  image: string
  tags: string[]
  highlights: string[]
  commuteScore: { short: number; medium: number; long: number }
  terrainScore: { flat: number; hilly: number; mixed: number }
  cargoScore: { none: number; sometimes: number; yes: number }
}

const BIKES: Bike[] = [
  {
    id: 'vmax-scooter',
    name: 'VX2 PRO GT',
    brand: 'VMAX',
    category: 'Electric Scooter',
    tagline: 'Compact, zippy, zero emissions',
    monthlyRate: 65,
    range: '40 mi',
    weight: '26 lbs',
    image:
      'https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/679149ae0c2d7fe8766dde67_vehicle-vmax-vx2.png',
    tags: ['Scooter', 'Electric', 'Compact'],
    highlights: ['Foldable for transit', 'Swappable battery', 'App-connected'],
    commuteScore: { short: 5, medium: 4, long: 2 },
    terrainScore: { flat: 5, hilly: 2, mixed: 3 },
    cargoScore: { none: 5, sometimes: 2, yes: 1 },
  },
  {
    id: 'trek-checkpoint',
    name: 'Checkpoint ALR 5',
    brand: 'Trek',
    category: 'Endurance Road Bike',
    tagline: 'All-terrain versatility, built to last',
    monthlyRate: 75,
    range: 'Pedal',
    weight: '21 lbs',
    image:
      'https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/68b8563e736aef72c829b1cd_trek-checkpoint-alr-5-gen-3-1242888-11.png',
    tags: ['Road', 'Gravel', 'Lightweight'],
    highlights: ['Carbon fork', 'IsoSpeed decoupler', 'All-season capable'],
    commuteScore: { short: 4, medium: 5, long: 5 },
    terrainScore: { flat: 4, hilly: 4, mixed: 5 },
    cargoScore: { none: 5, sometimes: 3, yes: 2 },
  },
  {
    id: 'turbo-vado',
    name: 'Turbo Vado SL',
    brand: 'Specialized',
    category: 'Commuter E-Bike',
    tagline: 'Fast, featherlight, city-ready',
    monthlyRate: 89,
    range: '60 mi',
    weight: '28 lbs',
    image:
      'https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/679149ae0c2d7fe8766dde67_vehicle-vmax-vx2.png',
    tags: ['Electric', 'Lightweight', 'Long Range'],
    highlights: ['SL 1.1 motor', 'Integrated lights', '60-mile range'],
    commuteScore: { short: 3, medium: 5, long: 5 },
    terrainScore: { flat: 4, hilly: 5, mixed: 5 },
    cargoScore: { none: 5, sometimes: 3, yes: 1 },
  },
  {
    id: 'cargowagen',
    name: 'Cargowagen NEO',
    brand: 'Cannondale',
    category: 'Cargo E-Bike',
    tagline: 'Haul everything, effortlessly',
    monthlyRate: 135,
    range: '45 mi',
    weight: '64 lbs',
    image:
      'https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/68b855ed28f4d8588ed497f5_C23_C67153U_Cargowagen_Neo_SGE_PD.jpg',
    tags: ['Cargo', 'Family', 'Electric'],
    highlights: ['400 lb payload', 'Child seat ready', 'Rain cover included'],
    commuteScore: { short: 4, medium: 5, long: 3 },
    terrainScore: { flat: 5, hilly: 3, mixed: 4 },
    cargoScore: { none: 2, sometimes: 5, yes: 5 },
  },
  {
    id: 'globe-haul',
    name: 'Globe Haul ST',
    brand: 'Specialized',
    category: 'Urban Cargo E-Bike',
    tagline: 'Your car replacement starts here',
    monthlyRate: 119,
    range: '50 mi',
    weight: '55 lbs',
    image:
      'https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/68b855ed28f4d8588ed497f5_C23_C67153U_Cargowagen_Neo_SGE_PD.jpg',
    tags: ['Cargo', 'Electric', 'Step-Through'],
    highlights: ['Low step frame', 'Rear rack 55 lb', 'Hydraulic brakes'],
    commuteScore: { short: 5, medium: 5, long: 4 },
    terrainScore: { flat: 5, hilly: 3, mixed: 4 },
    cargoScore: { none: 3, sometimes: 5, yes: 5 },
  },
  {
    id: 'brompton',
    name: 'C-Line Urban',
    brand: 'Brompton',
    category: 'Folding Bike',
    tagline: 'Folds in 20 seconds, fits anywhere',
    monthlyRate: 72,
    range: 'Pedal',
    weight: '25 lbs',
    image:
      'https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/68b8563e736aef72c829b1cd_trek-checkpoint-alr-5-gen-3-1242888-11.png',
    tags: ['Folding', 'Transit-Friendly', 'Compact'],
    highlights: ['Fits under desk', 'BART/Caltrain allowed', '6-speed'],
    commuteScore: { short: 5, medium: 4, long: 3 },
    terrainScore: { flat: 5, hilly: 3, mixed: 4 },
    cargoScore: { none: 5, sometimes: 3, yes: 1 },
  },
]

// ─── Quiz ─────────────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: 'commute',
    question: 'How far is your one-way commute?',
    options: [
      { label: 'Under 5 miles', value: 'short', emoji: '🏙️' },
      { label: '5 – 10 miles', value: 'medium', emoji: '🚦' },
      { label: 'Over 10 miles', value: 'long', emoji: '🛣️' },
    ],
  },
  {
    id: 'terrain',
    question: "What's your route like?",
    options: [
      { label: 'Flat city streets', value: 'flat', emoji: '🏢' },
      { label: 'Hilly & challenging', value: 'hilly', emoji: '⛰️' },
      { label: 'Mix of both', value: 'mixed', emoji: '🗺️' },
    ],
  },
  {
    id: 'cargo',
    question: 'Do you need to carry things?',
    options: [
      { label: 'Just my bag', value: 'none', emoji: '🎒' },
      { label: 'Groceries sometimes', value: 'sometimes', emoji: '🛍️' },
      { label: 'Kids or heavy loads', value: 'yes', emoji: '📦' },
    ],
  },
] as const

type CommuteAnswer = 'short' | 'medium' | 'long'
type TerrainAnswer = 'flat' | 'hilly' | 'mixed'
type CargoAnswer = 'none' | 'sometimes' | 'yes'

interface Answers {
  commute?: CommuteAnswer
  terrain?: TerrainAnswer
  cargo?: CargoAnswer
}

function recommendBikes(answers: Answers): Bike[] {
  const scores = BIKES.map((bike) => ({
    bike,
    score:
      bike.commuteScore[answers.commute ?? 'medium'] +
      bike.terrainScore[answers.terrain ?? 'flat'] +
      bike.cargoScore[answers.cargo ?? 'none'],
  }))
  scores.sort((a, b) => b.score - a.score)
  return scores.map((s) => s.bike)
}

// ─── Styled components ────────────────────────────────────────────────────────

const Wrapper = styled.div`
  width: 660px;
  max-width: 100%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const FixedHeader = styled.div`
  padding: 2rem 2rem 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex-shrink: 0;
`

const ScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const RpLogo = styled.img`
  height: 22px;
  object-fit: contain;
`

const PandaBadge = styled.div`
  background: ${rpGreenLight};
  color: ${rpGreen};
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const PandaIcon = styled.img`
  height: 1.125rem;
  width: auto;
  object-fit: contain;
`

const StipendBadge = styled.div`
  background: ${palette.accent.blue100};
  color: ${palette.accent.blue500};
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  margin-left: auto;
`

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid ${palette.grey.grey200};
  gap: 0;
`

const Tab = styled.button<{ active: boolean }>`
  padding: 0.625rem 1.25rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.active ? 700 : 500)};
  color: ${(p) => (p.active ? rpGreen : palette.text.secondary)};
  border-bottom: 2px solid ${(p) => (p.active ? rpGreen : 'transparent')};
  margin-bottom: -2px;
  transition: all 0.15s;

  &:hover {
    color: ${rpGreen};
  }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`

const Animated = styled.div`
  animation: ${fadeIn} 0.2s ease;
`

// ─── Catalog styles ───────────────────────────────────────────────────────────

const CatalogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.875rem;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const CatalogCard = styled.button<{ selected?: boolean }>`
  border: 2px solid ${(p) => (p.selected ? rpGreen : palette.grey.grey200)};
  border-radius: 0.875rem;
  background: ${(p) => (p.selected ? rpGreenLight : palette.white)};
  padding: 0;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  overflow: hidden;

  &:hover {
    border-color: ${rpGreen};
    box-shadow: 0 2px 8px rgba(22, 163, 74, 0.15);
  }
`

const CatalogImageBox = styled.div`
  background: ${rpGreenLight};
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
`

const CatalogImg = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`

const CatalogCardBody = styled.div`
  padding: 0.75rem;
`

const CatalogBrand = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${palette.text.placeholder};
  margin-bottom: 0.125rem;
`

const CatalogName = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${palette.text.primary};
  margin-bottom: 0.25rem;
  line-height: 1.2;
`

const CatalogCategory = styled.div`
  font-size: 0.6875rem;
  color: ${palette.text.secondary};
  margin-bottom: 0.5rem;
`

const CatalogPrice = styled.div<{ covered?: boolean }>`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${(p) => (p.covered ? rpGreen : palette.text.primary)};
`

const CatalogTag = styled.span`
  display: inline-block;
  background: ${rpGreenLight};
  color: ${rpGreen};
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 999px;
  margin-right: 0.25rem;
  margin-top: 0.375rem;
`

// ─── Detail panel (shown below catalog when a card is selected) ────────────────

const DetailPanel = styled.div`
  border: 1.5px solid ${rpGreenMid};
  border-radius: 1rem;
  overflow: hidden;
  background: ${palette.white};
`

const DetailImageArea = styled.div`
  background: ${rpGreenLight};
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`

const DetailImg = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`

const DetailBody = styled.div`
  padding: 1.125rem 1.25rem;
`

const CategoryPill = styled.span`
  background: ${rpGreenLight};
  color: ${rpGreen};
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  display: inline-block;
  margin-bottom: 0.375rem;
`

const SpecRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: 0.625rem 0;
`

const SpecPill = styled.span`
  background: ${palette.grey.grey100};
  color: ${palette.text.secondary};
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
`

const HighlightList = styled.ul`
  margin: 0.5rem 0 0.75rem 0;
  padding-left: 1.25rem;

  li {
    font-size: 0.8125rem;
    color: ${palette.text.secondary};
    margin-bottom: 0.25rem;
  }
`

const PricingBox = styled.div`
  background: ${palette.grey.grey50};
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const PricingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PricingLabel = styled.span`
  font-size: 0.8125rem;
  color: ${palette.text.secondary};
`

const PricingValue = styled.span<{ highlight?: boolean }>`
  font-size: 0.875rem;
  font-weight: ${(p) => (p.highlight ? 700 : 500)};
  color: ${(p) => (p.highlight ? rpGreen : palette.text.primary)};
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${palette.grey.grey200};
  margin: 0.125rem 0;
`

// ─── Quiz styles ──────────────────────────────────────────────────────────────

const ProgressBar = styled.div<{ step: number; total: number }>`
  height: 4px;
  border-radius: 999px;
  background: ${palette.grey.grey200};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(p) => (p.step / p.total) * 100}%;
    background: ${rpGreen};
    border-radius: 999px;
    transition: width 0.3s ease;
  }
`

const OptionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const OptionButton = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border: 2px solid ${(p) => (p.selected ? rpGreen : palette.grey.grey200)};
  border-radius: 0.75rem;
  background: ${(p) => (p.selected ? rpGreenLight : palette.white)};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  width: 100%;

  &:hover {
    border-color: ${rpGreen};
    background: ${rpGreenLight};
  }
`

const OptionEmoji = styled.span`
  font-size: 1.5rem;
  flex-shrink: 0;
`

const OptionLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${palette.text.primary};
`

// ─── Recommendation styles ────────────────────────────────────────────────────

const RecoCardLarge = styled.div`
  border: 1.5px solid ${rpGreenMid};
  border-radius: 1rem;
  overflow: hidden;
`

const RecoImageArea = styled.div`
  background: ${rpGreenLight};
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`

const RecoImg = styled.img`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`

const AltRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
`

const AltCard = styled.button<{ selected?: boolean }>`
  border: 1.5px solid ${(p) => (p.selected ? rpGreen : palette.grey.grey200)};
  border-radius: 0.75rem;
  background: ${(p) => (p.selected ? rpGreenLight : palette.white)};
  padding: 0.75rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;

  &:hover {
    border-color: ${rpGreen};
  }
`

const AltImg = styled.img`
  width: 100%;
  height: 60px;
  object-fit: contain;
  margin-bottom: 0.375rem;
`

// ─── Success styles ───────────────────────────────────────────────────────────

const SuccessCircle = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background: ${rpGreenLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
`

// ─── Component ────────────────────────────────────────────────────────────────

interface RidePandaModalProps {
  open: boolean
  onClose: () => void
}

type Step = 'catalog' | 'quiz' | 'recommendation' | 'success'

const RidePandaModal: React.FC<RidePandaModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<Step>('catalog')
  const [activeTab, setActiveTab] = useState<'catalog' | 'quiz'>('catalog')
  const [questionIndex, setQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Answers>({})
  const [rankedBikes, setRankedBikes] = useState<Bike[]>([])
  const [selectedBikeId, setSelectedBikeId] = useState<string>('')

  const currentQuestion = QUESTIONS[questionIndex]
  const selectedBike =
    rankedBikes.find((b) => b.id === selectedBikeId) ??
    rankedBikes[0] ??
    (step === 'catalog' && selectedBikeId
      ? BIKES.find((b) => b.id === selectedBikeId)
      : undefined)
  const catalogSelected = BIKES.find((b) => b.id === selectedBikeId)

  const outOfPocket = selectedBike
    ? Math.max(0, selectedBike.monthlyRate - EMPLOYER_STIPEND)
    : 0

  const handleOptionSelect = (value: string) => {
    const key = currentQuestion.id as keyof Answers
    const updated = { ...answers, [key]: value }
    setAnswers(updated)
    if (questionIndex < QUESTIONS.length - 1) {
      setTimeout(() => setQuestionIndex(questionIndex + 1), 180)
    } else {
      const ranked = recommendBikes(updated as Required<Answers>)
      setRankedBikes(ranked)
      setSelectedBikeId(ranked[0].id)
      setStep('recommendation')
    }
  }

  const handleClose = (_e?: React.MouseEvent<any>) => {
    setStep('catalog')
    setActiveTab('catalog')
    setQuestionIndex(0)
    setAnswers({})
    setRankedBikes([])
    setSelectedBikeId('')
    onClose()
  }

  const switchTab = (tab: 'catalog' | 'quiz') => {
    setActiveTab(tab)
    setStep(tab)
    if (tab === 'quiz') {
      setQuestionIndex(0)
      setAnswers({})
    }
  }

  const renderCatalog = () => (
    <Animated key='catalog'>
      <CatalogGrid>
        {BIKES.map((bike) => {
          const covered = bike.monthlyRate <= EMPLOYER_STIPEND
          return (
            <CatalogCard
              key={bike.id}
              selected={selectedBikeId === bike.id}
              onClick={() =>
                setSelectedBikeId(
                  selectedBikeId === bike.id ? '' : bike.id
                )
              }
            >
              <CatalogImageBox>
                <CatalogImg
                  src={bike.image}
                  alt={bike.name}
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.opacity = '0.2'
                  }}
                />
              </CatalogImageBox>
              <CatalogCardBody>
                <CatalogBrand>{bike.brand}</CatalogBrand>
                <CatalogName>{bike.name}</CatalogName>
                <CatalogCategory>{bike.category}</CatalogCategory>
                <CatalogPrice covered={covered}>
                  {covered ? '✓ Fully covered' : `$${bike.monthlyRate}/mo`}
                </CatalogPrice>
                {!covered && (
                  <div
                    style={{
                      fontSize: '0.6875rem',
                      color: palette.text.placeholder,
                    }}
                  >
                    ${bike.monthlyRate - EMPLOYER_STIPEND}/mo after stipend
                  </div>
                )}
                <div>
                  {bike.tags.slice(0, 2).map((t) => (
                    <CatalogTag key={t}>{t}</CatalogTag>
                  ))}
                </div>
              </CatalogCardBody>
            </CatalogCard>
          )
        })}
      </CatalogGrid>

      {catalogSelected && (
        <Animated key={catalogSelected.id}>
          <DetailPanel>
            <DetailImageArea>
              <DetailImg
                src={catalogSelected.image}
                alt={catalogSelected.name}
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.opacity = '0.2'
                }}
              />
            </DetailImageArea>
            <DetailBody>
              <CategoryPill>{catalogSelected.category}</CategoryPill>
              <Text
                variant='body1'
                style={{ fontWeight: 700, fontSize: '1.0625rem' }}
              >
                {catalogSelected.brand} {catalogSelected.name}
              </Text>
              <Text
                variant='body2'
                style={{ color: palette.text.secondary, marginTop: '0.25rem' }}
              >
                {catalogSelected.tagline}
              </Text>
              <SpecRow>
                {catalogSelected.tags.map((tag) => (
                  <SpecPill key={tag}>{tag}</SpecPill>
                ))}
                {catalogSelected.range !== 'Pedal' && (
                  <SpecPill>⚡ {catalogSelected.range} range</SpecPill>
                )}
                <SpecPill>⚖️ {catalogSelected.weight}</SpecPill>
              </SpecRow>
              <Text
                variant='body2'
                style={{
                  fontWeight: 600,
                  color: palette.text.primary,
                  marginBottom: '0.25rem',
                }}
              >
                What's included
              </Text>
              <HighlightList>
                {catalogSelected.highlights.map((h) => (
                  <li key={h}>{h}</li>
                ))}
                <li>Theft insurance</li>
                <li>Free tune-ups & maintenance</li>
                <li>Helmet, lock & pump welcome kit</li>
              </HighlightList>
              <PricingBox>
                <PricingRow>
                  <PricingLabel>Monthly rate</PricingLabel>
                  <PricingValue>${catalogSelected.monthlyRate}/mo</PricingValue>
                </PricingRow>
                <PricingRow>
                  <PricingLabel>OpenAI stipend</PricingLabel>
                  <PricingValue style={{ color: rpGreen }}>
                    −$
                    {Math.min(EMPLOYER_STIPEND, catalogSelected.monthlyRate)}/mo
                  </PricingValue>
                </PricingRow>
                <Divider />
                <PricingRow>
                  <PricingLabel style={{ fontWeight: 600 }}>
                    You pay
                  </PricingLabel>
                  <PricingValue highlight>
                    {catalogSelected.monthlyRate <= EMPLOYER_STIPEND
                      ? 'Fully covered 🎉'
                      : `$${catalogSelected.monthlyRate - EMPLOYER_STIPEND}/mo`}
                  </PricingValue>
                </PricingRow>
              </PricingBox>
            </DetailBody>
          </DetailPanel>

          <PrimaryButton
            onClick={() => setStep('success')}
            style={{
              width: '100%',
              background: rpGreen,
              borderColor: rpGreen,
              marginTop: '0.75rem',
            }}
          >
            Enroll in {catalogSelected.brand} {catalogSelected.name}
          </PrimaryButton>
        </Animated>
      )}

      {!catalogSelected && (
        <div
          style={{
            textAlign: 'center',
            padding: '0.5rem 0',
            color: palette.text.placeholder,
            fontSize: '0.8125rem',
          }}
        >
          Select a bike above to see details and pricing
        </div>
      )}
    </Animated>
  )

  const renderQuiz = () => (
    <Animated key={questionIndex}>
      <ProgressBar step={questionIndex + 1} total={QUESTIONS.length} />
      <div style={{ marginTop: '1.5rem' }}>
        <Text
          variant='body2'
          style={{ color: palette.text.placeholder, marginBottom: '0.5rem' }}
        >
          Question {questionIndex + 1} of {QUESTIONS.length}
        </Text>
        <Text
          variant='body1'
          style={{
            fontWeight: 700,
            fontSize: '1.125rem',
            marginBottom: '1.25rem',
          }}
        >
          {currentQuestion.question}
        </Text>
        <OptionGrid>
          {currentQuestion.options.map((opt) => (
            <OptionButton
              key={opt.value}
              selected={
                answers[currentQuestion.id as keyof Answers] === opt.value
              }
              onClick={() => handleOptionSelect(opt.value)}
            >
              <OptionEmoji>{opt.emoji}</OptionEmoji>
              <OptionLabel>{opt.label}</OptionLabel>
            </OptionButton>
          ))}
        </OptionGrid>
      </div>
    </Animated>
  )

  const renderRecommendation = () => {
    const topBike =
      rankedBikes.find((b) => b.id === selectedBikeId) ?? rankedBikes[0]
    if (!topBike) return null
    const oop = Math.max(0, topBike.monthlyRate - EMPLOYER_STIPEND)
    return (
      <Animated key='recommendation'>
        <Text
          variant='body1'
          style={{ fontWeight: 700, fontSize: '1.125rem', marginBottom: '0.25rem' }}
        >
          Your perfect match ✨
        </Text>
        <Text
          variant='body2'
          style={{ color: palette.text.secondary, marginBottom: '1rem' }}
        >
          Based on your commute profile:
        </Text>

        <RecoCardLarge>
          <RecoImageArea>
            <RecoImg
              src={topBike.image}
              alt={topBike.name}
              onError={(e) => {
                ;(e.target as HTMLImageElement).style.opacity = '0.2'
              }}
            />
          </RecoImageArea>
          <DetailBody>
            <CategoryPill>{topBike.category}</CategoryPill>
            <Text
              variant='body1'
              style={{ fontWeight: 700, fontSize: '1.0625rem' }}
            >
              {topBike.brand} {topBike.name}
            </Text>
            <Text
              variant='body2'
              style={{ color: palette.text.secondary, marginTop: '0.25rem' }}
            >
              {topBike.tagline}
            </Text>
            <SpecRow>
              {topBike.tags.map((tag) => (
                <SpecPill key={tag}>{tag}</SpecPill>
              ))}
              {topBike.range !== 'Pedal' && (
                <SpecPill>⚡ {topBike.range} range</SpecPill>
              )}
              <SpecPill>⚖️ {topBike.weight}</SpecPill>
            </SpecRow>
            <HighlightList>
              {topBike.highlights.map((h) => (
                <li key={h}>{h}</li>
              ))}
              <li>Theft insurance & free maintenance</li>
              <li>Welcome kit (helmet, lock, pump)</li>
            </HighlightList>
            <PricingBox>
              <PricingRow>
                <PricingLabel>Monthly rate</PricingLabel>
                <PricingValue>${topBike.monthlyRate}/mo</PricingValue>
              </PricingRow>
              <PricingRow>
                <PricingLabel>OpenAI stipend</PricingLabel>
                <PricingValue style={{ color: rpGreen }}>
                  −${Math.min(EMPLOYER_STIPEND, topBike.monthlyRate)}/mo
                </PricingValue>
              </PricingRow>
              <Divider />
              <PricingRow>
                <PricingLabel style={{ fontWeight: 600 }}>You pay</PricingLabel>
                <PricingValue highlight>
                  {oop === 0 ? 'Fully covered 🎉' : `$${oop}/mo`}
                </PricingValue>
              </PricingRow>
            </PricingBox>
          </DetailBody>
        </RecoCardLarge>

        {rankedBikes.length > 1 && (
          <>
            <Text
              variant='body2'
              style={{
                color: palette.text.placeholder,
                textTransform: 'uppercase',
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
              }}
            >
              Other options
            </Text>
            <AltRow>
              {rankedBikes.slice(1, 4).map((bike) => (
                <AltCard
                  key={bike.id}
                  selected={bike.id === selectedBikeId}
                  onClick={() => setSelectedBikeId(bike.id)}
                >
                  <AltImg
                    src={bike.image}
                    alt={bike.name}
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                  <div
                    style={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: palette.text.placeholder,
                    }}
                  >
                    {bike.brand}
                  </div>
                  <div
                    style={{ fontSize: '0.75rem', fontWeight: 700, color: palette.text.primary }}
                  >
                    {bike.name}
                  </div>
                  <div
                    style={{ fontSize: '0.75rem', fontWeight: 700, color: rpGreen, marginTop: '0.25rem' }}
                  >
                    ${bike.monthlyRate}/mo
                  </div>
                </AltCard>
              ))}
            </AltRow>
          </>
        )}

        <FlexContainer style={{ gap: '0.75rem', marginTop: '0.75rem' }}>
          <SecondaryButton
            onClick={() => {
              setStep('quiz')
              setActiveTab('quiz')
              setQuestionIndex(0)
              setAnswers({})
            }}
            style={{ flex: 1 }}
          >
            Retake quiz
          </SecondaryButton>
          <PrimaryButton
            onClick={() => setStep('success')}
            style={{
              flex: 2,
              background: rpGreen,
              borderColor: rpGreen,
            }}
          >
            Enroll — it's free to start
          </PrimaryButton>
        </FlexContainer>
      </Animated>
    )
  }

  const renderSuccess = () => {
    const bike = selectedBike
    const oop = bike ? Math.max(0, bike.monthlyRate - EMPLOYER_STIPEND) : 0
    return (
      <Animated key='success'>
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <SuccessCircle>
            <svg
              width='36'
              height='36'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M20 6L9 17L4 12'
                stroke={rpGreen}
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </SuccessCircle>
          <Text
            variant='body1'
            style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '0.5rem' }}
          >
            You&apos;re enrolled!{' '}
            <PandaIcon
              src='https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/64a5bc22ae60bba4a7051a68_Panda%20-%20Peek%20Yellow.svg'
              alt='RidePanda'
              style={{ height: '1.5rem', verticalAlign: 'middle' }}
            />
          </Text>
          <Text
            variant='body2'
            style={{
              color: palette.text.secondary,
              maxWidth: '22rem',
              margin: '0 auto 1.5rem auto',
              lineHeight: 1.6,
            }}
          >
            RidePanda will contact you within 24 hours to schedule pickup at a
            local PandaHub or ship directly to your door.
          </Text>
          {bike && (
            <div
              style={{
                background: rpGreenLight,
                borderRadius: '0.875rem',
                padding: '1rem 1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: '2rem' }}>🚲</span>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: palette.text.primary,
                  }}
                >
                  {bike.brand} {bike.name}
                </div>
                <div
                  style={{ fontSize: '0.8125rem', color: rpGreen, fontWeight: 600 }}
                >
                  {oop === 0
                    ? 'Fully covered by OpenAI'
                    : `$${oop}/mo after stipend`}
                </div>
              </div>
            </div>
          )}
          <PrimaryButton
            onClick={handleClose}
            style={{ width: '100%', background: rpGreen, borderColor: rpGreen }}
          >
            Done
          </PrimaryButton>
        </div>
      </Animated>
    )
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Wrapper>
        <FixedHeader>
          <BrandRow>
            <RpLogo
              src='https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/69cad0b6bfe9156a56384a01_Color%3DBlack%2C%20Version%3DNew.png'
              alt='RidePanda'
              onError={(e) => {
                ;(e.target as HTMLImageElement).style.display = 'none'
              }}
            />
            <PandaBadge>
              <PandaIcon
                src='https://cdn.prod.website-files.com/647a6268bb3d6fefb99a9128/64a5bc22ae60bba4a7051a68_Panda%20-%20Peek%20Yellow.svg'
                alt='RidePanda'
              />
              Micromobility
            </PandaBadge>
            <StipendBadge>💰 $100/mo stipend</StipendBadge>
          </BrandRow>

          {step !== 'success' && (
            <TabBar>
              <Tab
                active={step === 'catalog' || step === 'recommendation'}
                onClick={() => switchTab('catalog')}
              >
                Browse catalog
              </Tab>
              <Tab
                active={step === 'quiz'}
                onClick={() => switchTab('quiz')}
              >
                ✨ Find my perfect bike
              </Tab>
            </TabBar>
          )}
        </FixedHeader>

        <ScrollBody>
          {step === 'catalog' && renderCatalog()}
          {step === 'quiz' && renderQuiz()}
          {step === 'recommendation' && renderRecommendation()}
          {step === 'success' && renderSuccess()}
        </ScrollBody>
      </Wrapper>
    </Modal>
  )
}

export default RidePandaModal
